// UI Onboarding: orchestratore init post-login + welcome wizard +
// email consent + legal acceptance. PR-10 (ADR-0005). Estratto a parità
// funzionale da coach/index.html (welcome 542-636, consent 665-734,
// legal 736-841, loadUserData 843-914).
//
// Dipendenze ESM:
//   builders.js   → saveProfile NON serve (le scritture qui sono dirette
//                   su sb.from('profiles').update). Nessun import.
//   ui-profile.js → loadMeasurements (init: ultima misurazione)
//   ui-home.js    → updateBadge, updateDL, renderHomeDayCard,
//                   renderHomeMiniChart (refresh UI a init)
//   ui-chat.js    → hydrateOnboardingConv, addMsg, openChat (welcome → chat
//                   onboarding, ripresa conv da localStorage post-refresh)
//
// Dipendenze runtime (window.* / global scope chain):
//   window.S            → S.profile, S.plans, S.conv (orchestratore init)
//   window.sb           → client Supabase (write profili.onboarding_flags)
//   window.currentUser  → utente loggato (gating)
//   window._welcomeFlags → copia locale di profiles.onboarding_flags
//                          (esposta come window.* perché il listener
//                          inline `sb.auth.onAuthStateChange` la legge
//                          via bare identifier `_welcomeFlags`)
//
// NB: loadUserData è chiamata dal listener inline `sb.auth.onAuthStateChange`
// come bare identifier `loadUserData()` → resolve via window dopo Object.assign
// in main.js. Stesso pattern per le funzioni open/close chiamate dagli
// onclick handler nello HTML (welcomeNext, goToSlide, closeWelcome,
// closeEmailConsent, acceptLegal, rejectLegalAndLogout, ecc.).

import { clearAuthSession } from './core.js';
import { clearRecipeCache } from './meal-recipe-composer.js';
import { loadMeasurements } from './ui-profile.js';
import { updateBadge, updateDL, renderHomeDayCard, renderHomeMiniChart } from './ui-home.js';
import { hydrateOnboardingConv, addMsg, openChat } from './ui-chat.js';

// ─── WELCOME WIZARD ───────────────────────────────────────────────────────
// Modale di benvenuto al primo login. Stato persistito in profiles.onboarding_flags
// (JSONB su DB, vedi migration v2_profiles_onboarding_flags).
let _welcomeCurrentSlide = 0;
const WELCOME_SLIDE_COUNT = 4; // v1.12.0: 4 slide refresh (era 3, vedi docs/release/v1.12.0/03-wizard.md)

export function shouldShowWelcome(profile) {
  // Utente nuovo: nessuna riga in `profiles` finché non emette [PROFILO]. Fallback
  // su localStorage per non perdere il welcome al primo login. La migrazione al
  // JSONB avviene a saveProfile (vedi merge di onboarding_flags lì).
  if (!profile) {
    if (!window.currentUser) return false;
    return localStorage.getItem(`coachai_welcome_v1_${window.currentUser.id}`) !== '1';
  }
  const flags = profile.onboarding_flags || {};
  return flags.welcome_v1 !== true;
}

export function openWelcome() {
  _welcomeCurrentSlide = 0;
  _renderWelcomeSlide();
  document.getElementById('welcomeModal').classList.add('show');
  // Focus sul CTA per a11y/keyboard nav
  setTimeout(() => document.getElementById('welcomeCta')?.focus(), 50);
}

export async function closeWelcome() {
  document.getElementById('welcomeModal').classList.remove('show');
  // Marca flag su DB. UX non bloccante: se la scrittura fallisce, log e basta;
  // l'utente vedrà di nuovo il welcome al prossimo login (accettabile).
  await _markWelcomeFlag('welcome_v1');
}

export async function _markWelcomeFlag(flagName) {
  if (!window.currentUser) {
    console.warn('[welcome] markFlag without currentUser, skip');
    return;
  }
  const newFlags = { ...window._welcomeFlags, [flagName]: true };
  // Utente nuovo (pre-onboarding): non c'è ancora riga in profiles, scrivo in
  // localStorage. La migrazione al JSONB avviene a saveProfile (vedi merge lì).
  if (!window.S.profile) {
    try { localStorage.setItem(`coachai_${flagName}_${window.currentUser.id}`, '1'); } catch (_) {}
    window._welcomeFlags = newFlags;
    return;
  }
  const { error } = await window.sb.from('profiles')
    .update({ onboarding_flags: newFlags })
    .eq('id', window.currentUser.id);
  if (error) {
    console.warn('[welcome] flag write failed:', error.message);
    return;
  }
  window._welcomeFlags = newFlags;
  window.S.profile.onboarding_flags = newFlags;
}

export function goToSlide(n) {
  if (n < 0 || n >= WELCOME_SLIDE_COUNT) return;
  _welcomeCurrentSlide = n;
  _renderWelcomeSlide();
}

export function welcomeNext() {
  if (_welcomeCurrentSlide < WELCOME_SLIDE_COUNT - 1) {
    goToSlide(_welcomeCurrentSlide + 1);
  } else {
    // "Iniziamo" → chiudi modale (flag scritto async in background).
    // Se serve ancora il consenso email, chiedilo prima di aprire la chat.
    // Per utenti nuovi (S.profile null) il check usa localStorage come fallback.
    closeWelcome();
    if (shouldShowEmailConsent(window.S.profile || null)) {
      openEmailConsent({ openChatAfter: true });
    } else {
      openChat();
    }
  }
}

export function _renderWelcomeSlide() {
  document.querySelectorAll('#welcomeModal .welcome-slide').forEach(el => {
    const idx = parseInt(el.dataset.slide, 10);
    el.classList.toggle('is-current', idx === _welcomeCurrentSlide);
  });
  document.querySelectorAll('#welcomeModal .welcome-dot').forEach(el => {
    const idx = parseInt(el.dataset.dot, 10);
    el.classList.toggle('is-active', idx === _welcomeCurrentSlide);
  });
  const cta = document.getElementById('welcomeCta');
  if (cta) {
    cta.textContent = _welcomeCurrentSlide === WELCOME_SLIDE_COUNT - 1 ? 'Iniziamo →' : 'Avanti →';
  }
}

// ─── EMAIL CONSENT ─────────────────────────────────────────────────────────
// Modale di consenso email aggiornamenti prodotto. Stato persistito nello
// stesso JSONB profiles.onboarding_flags (chiavi: email_consent bool +
// email_consent_at ISO timestamp per audit GDPR).
// Trigger: al login se email_consent è undefined/null. Per nuovi utenti
// appare DOPO il welcome wizard (vedi welcomeNext) prima di aprire la chat.
let _consentOpenChatAfter = false;

export function shouldShowEmailConsent(profile) {
  // Utente nuovo (no riga in profiles): fallback su localStorage. Presenza della
  // chiave = scelta già espressa (vedi _markConsentFlag). Migrazione → JSONB a
  // saveProfile.
  if (!profile) {
    if (!window.currentUser) return false;
    return localStorage.getItem(`coachai_email_consent_${window.currentUser.id}`) === null;
  }
  const flags = profile.onboarding_flags || {};
  return flags.email_consent === undefined || flags.email_consent === null;
}

export function openEmailConsent({ openChatAfter = false } = {}) {
  _consentOpenChatAfter = openChatAfter;
  document.getElementById('consentModal').classList.add('show');
}

export async function closeEmailConsent(accepted) {
  document.getElementById('consentModal').classList.remove('show');
  // Scrittura flag UX-non-bloccante: se fallisce log e basta, l'utente vedrà
  // di nuovo il consent al prossimo login (accettabile).
  await _markConsentFlag(accepted);
  // Apertura chat solo se richiesta dall'opener (es. flusso post-welcome)
  if (_consentOpenChatAfter) {
    _consentOpenChatAfter = false;
    openChat();
    // NB: nel flusso post-welcome di un nuovo utente, l'annuncio non appare
    // a questo login (siamo già stati pesanti con welcome+consent). Riapparirà
    // al prossimo login quando shouldShowAnnouncement scatta da loadUserData.
    return;
  }
  // Consent espresso senza welcome (utente esistente che non aveva mai scelto):
  // dopo aver chiuso, valuta l'annuncio in coda alla chain.
  if (shouldShowAnnouncement(window.S.profile || null)) {
    openAnnouncement();
  }
}

export async function _markConsentFlag(accepted) {
  if (!window.currentUser) {
    console.warn('[consent] markFlag without currentUser, skip');
    return;
  }
  const at = new Date().toISOString();
  const newFlags = {
    ...window._welcomeFlags,
    email_consent: accepted,
    email_consent_at: at
  };
  // Utente nuovo (pre-onboarding): persisto in localStorage; saveProfile farà
  // il merge in profiles.onboarding_flags al primo [PROFILO] salvato.
  if (!window.S.profile) {
    try {
      localStorage.setItem(
        `coachai_email_consent_${window.currentUser.id}`,
        JSON.stringify({ accepted, at })
      );
    } catch (_) {}
    window._welcomeFlags = newFlags;
    return;
  }
  const { error } = await window.sb.from('profiles')
    .update({ onboarding_flags: newFlags })
    .eq('id', window.currentUser.id);
  if (error) {
    console.warn('[consent] flag write failed:', error.message);
    return;
  }
  window._welcomeFlags = newFlags;
  window.S.profile.onboarding_flags = newFlags;
}

// ─── LEGAL ACCEPTANCE (Privacy + Terms + Disclaimer + 18+) ──────────────────
// Stato persistito in profiles.onboarding_flags (chiavi: legal_acceptance_v1
// bool + legal_acceptance_at ISO timestamp per audit GDPR / probatorio).
//
// Trigger:
//   - Utenti NUOVI: checkbox al signup → localStorage `coachai_legal_v1_pending`
//     → migrato a JSONB al primo saveProfile() (idempotente).
//   - Utenti ESISTENTI senza il flag: modal forzato post-login (no close-on-Esc,
//     no click-outside). Solo 2 esiti: accetta → DB + continua, rifiuta → logout.
//
// Versionato `_v1`: bumpiamo a `_v2` se i documenti legali cambiano in modo
// sostanziale e vogliamo ri-richiedere l'accettazione a tutti gli utenti.

export function shouldShowLegalAcceptance(profile) {
  // Utente nuovo (no riga in profiles): la checkbox di signup ha già scritto
  // `coachai_legal_v1_pending` in localStorage. Se manca il flag E manca il
  // pending → forza il modal (caso edge: utente entrato senza passare dal
  // signup di questa versione, es. magic link da reset password).
  if (!profile) {
    try {
      return localStorage.getItem('coachai_legal_v1_pending') === null;
    } catch {
      return true;
    }
  }
  const flags = profile.onboarding_flags || {};
  return flags.legal_acceptance_v1 !== true;
}

export function openLegalAcceptance() {
  const m = document.getElementById('legalModal');
  if (!m) return;
  // Reset stato: checkbox unchecked, bottone Accetto disabilitato finché non spunta
  document.getElementById('legalModalCheck').checked = false;
  const acceptBtn = document.getElementById('legalModalAcceptBtn');
  acceptBtn.disabled = true;
  acceptBtn.style.opacity = '0.45';
  document.getElementById('legalModalCheck').onchange = (ev) => {
    const ok = ev.target.checked;
    acceptBtn.disabled = !ok;
    acceptBtn.style.opacity = ok ? '1' : '0.45';
  };
  m.classList.add('show');
}

export async function acceptLegal() {
  const checked = document.getElementById('legalModalCheck').checked;
  if (!checked) return;
  const btn = document.getElementById('legalModalAcceptBtn');
  btn.disabled = true; btn.textContent = 'Salvataggio…';
  const ok = await _markLegalAcceptanceFlag(true);
  if (!ok) {
    btn.disabled = false; btn.textContent = 'Accetto e continuo';
    return;
  }
  document.getElementById('legalModal').classList.remove('show');
  // Continua la chain di onboarding (legal era il primo if/else if in
  // loadUserData → ha bypassato welcome/email-consent/announcement al primo
  // login). Riavvia il check qui dopo il salvataggio del flag.
  if (shouldShowWelcome(window.S.profile || null)) {
    openWelcome();
  } else if (shouldShowEmailConsent(window.S.profile || null)) {
    openEmailConsent();
  } else if (shouldShowAnnouncement(window.S.profile || null)) {
    openAnnouncement();
  }
}

export async function rejectLegalAndLogout() {
  // Logout immediato e cancellazione token. Pattern identico a confirmLogout()
  // ma senza modal di conferma (l'utente ha già scelto di rifiutare i Termini).
  clearRecipeCache();
  clearAuthSession();
  try { window.sb.auth.signOut({ scope: 'local' }); } catch (_) {}
  location.reload();
}

export async function _markLegalAcceptanceFlag(accepted) {
  if (!window.currentUser) {
    console.warn('[legal] markFlag without currentUser, skip');
    return false;
  }
  const at = new Date().toISOString();
  const newFlags = {
    ...window._welcomeFlags,
    legal_acceptance_v1: accepted,
    legal_acceptance_at: at
  };
  // Utente nuovo (pre-saveProfile): aggiorno solo localStorage. saveProfile
  // farà il merge in profiles.onboarding_flags al primo [PROFILO] salvato.
  if (!window.S.profile) {
    try {
      localStorage.setItem('coachai_legal_v1_pending', JSON.stringify({
        accepted, at, v: 1
      }));
    } catch (_) {}
    window._welcomeFlags = newFlags;
    return true;
  }
  const { error } = await window.sb.from('profiles')
    .update({ onboarding_flags: newFlags })
    .eq('id', window.currentUser.id);
  if (error) {
    console.warn('[legal] flag write failed:', error.message);
    return false;
  }
  window._welcomeFlags = newFlags;
  window.S.profile.onboarding_flags = newFlags;
  return true;
}

// ─── ANNOUNCEMENT ──────────────────────────────────────────────────────────
// Last announcement post-login: popup "Cosa c'è di nuovo" dismissable per
// versione. Versionato come welcome_v1 / legal_acceptance_v1: chiave dinamica
// `announcement_v{ANNOUNCEMENT_VERSION}` in profiles.onboarding_flags JSONB.
// Bump manuale di ANNOUNCEMENT_VERSION (in coach/index.html script inline)
// fa rivedere il modal a tutti gli utenti al prossimo login.
//
// Skip naturale se altri modal sono pending: la chain `else if` in
// loadUserData (e in acceptLegal/closeEmailConsent) garantisce mutex.
// L'annuncio appare solo al login in cui legal/welcome/consent sono già
// stati visti.

export function shouldShowAnnouncement(profile) {
  const v = window.ANNOUNCEMENT_VERSION;
  if (!v) return false; // safety: niente versione → niente popup
  const key = `announcement_v${v}`;
  if (!profile) {
    if (!window.currentUser) return false;
    return localStorage.getItem(`coachai_${key}_${window.currentUser.id}`) !== '1';
  }
  const flags = profile.onboarding_flags || {};
  return flags[key] !== true;
}

export function openAnnouncement() {
  renderAnnouncementBody();
  document.getElementById('announcementModal').classList.add('show');
  // Focus sul bottone OK per a11y/keyboard nav (pattern identico a consent)
  setTimeout(() => {
    document.querySelector('#announcementModal .btn-primary')?.focus();
  }, 50);
}

export function renderAnnouncementBody() {
  // Legge ANNOUNCEMENT_BODY_MD (definita in coach/index.html script inline,
  // esposta su window). Parser parseLightMarkdown vive in coach/src/core.js
  // ed è esposto su window via main.js bridge.
  const src = window.ANNOUNCEMENT_BODY_MD || '';
  const html = (window.parseLightMarkdown || (s => s))(src);
  const target = document.getElementById('announcementBody');
  if (target) target.innerHTML = html;
}

export async function closeAnnouncement() {
  document.getElementById('announcementModal').classList.remove('show');
  // UX-non-bloccante: se la scrittura DB fallisce, riappare al prossimo login.
  await _markAnnouncementFlag();
}

export async function _markAnnouncementFlag() {
  if (!window.currentUser) {
    console.warn('[announcement] markFlag without currentUser, skip');
    return;
  }
  const v = window.ANNOUNCEMENT_VERSION;
  if (!v) return;
  const key = `announcement_v${v}`;
  const at = new Date().toISOString();
  const newFlags = {
    ...window._welcomeFlags,
    [key]: true,
    [`${key}_at`]: at
  };
  // Utente nuovo (pre-saveProfile): persisto in localStorage. saveProfile
  // farà il merge in profiles.onboarding_flags al primo [PROFILO] salvato.
  if (!window.S.profile) {
    try {
      localStorage.setItem(`coachai_${key}_${window.currentUser.id}`, '1');
    } catch (_) {}
    window._welcomeFlags = newFlags;
    return;
  }
  const { error } = await window.sb.from('profiles')
    .update({ onboarding_flags: newFlags })
    .eq('id', window.currentUser.id);
  if (error) {
    console.warn('[announcement] flag write failed:', error.message);
    return;
  }
  window._welcomeFlags = newFlags;
  window.S.profile.onboarding_flags = newFlags;
}

// ─── LOAD / SAVE DB ────────────────────────────────────────────────────────
export async function loadUserData() {
  if (!window.currentUser) return;

  // Carica profilo
  const { data: prof } = await window.sb.from('profiles').select('*').eq('id', window.currentUser.id).single();
  if (prof) {
    window.S.profile = prof;
    window._welcomeFlags = prof.onboarding_flags || {};
    updateBadge();
  }
  // Legal acceptance ha priorità su tutto: nessuna interazione finché Privacy +
  // Termini + Disclaimer + 18+ non sono confermati. Per utenti NUOVI con flag
  // pendente in localStorage (signup post-2026-05-06) → no modal, migrazione a
  // saveProfile. Per utenti ESISTENTI → modal forzato (close-on-Esc disabilitato).
  if (shouldShowLegalAcceptance(prof || null)) {
    openLegalAcceptance();
  } else if (shouldShowWelcome(prof || null)) {
    // Welcome / email consent: valutati SEMPRE, anche se prof è null (utente
    // nuovo pre-onboarding). I check usano localStorage come fallback finché
    // il record profiles non esiste; saveProfile farà la migrazione al JSONB.
    openWelcome();
    // Il consent viene chiesto da welcomeNext alla fine del wizard.
  } else if (shouldShowEmailConsent(prof || null)) {
    // Welcome già visto ma consenso ancora non espresso: chiedi al login,
    // senza aprire chat dopo (resta sulla home / chat di onboarding).
    openEmailConsent();
  } else if (shouldShowAnnouncement(prof || null)) {
    // Announcement skip naturale se uno dei precedenti scatta: appare solo
    // se la chain di onboarding/consent è completa. Riappare al prossimo
    // login finché non dismissato (vedi shouldShowAnnouncement + _markAnnouncementFlag).
    openAnnouncement();
  }

  // Carica piani. Includo id per poter risalire al plan_id del meal plan
  // (serve per shopping_list_view via genSpesa).
  const { data: plans } = await window.sb.from('plans')
    .select('id, tipo, data, created_at')
    .eq('user_id', window.currentUser.id)
    .order('updated_at', { ascending: false });
  if (plans && plans.length) {
    const seen = new Set();
    plans.forEach(p => {
      if (!seen.has(p.tipo)) {
        window.S.plans[p.tipo] = p.data;
        // Preserva id al primo livello per accesso successivo:
        //   - meal → spesa (genSpesa, BKL-005)
        //   - meal/workout/stretch → *_completions persistence (BKL-005/BKL-036)
        // BKL-036 fix 2026-05-15: prima settava _id solo per `meal`, lasciando
        // `workout._id` e `stretch._id` undefined → handler completion iframe
        // saltava upsert/select silently.
        if (window.S.plans[p.tipo] && typeof window.S.plans[p.tipo] === 'object') {
          window.S.plans[p.tipo]._id = p.id;
          // _createdAt: usato da plan-helpers.js per derivare current_phase/week.
          // Vedi spec 2026-05-15-home-widgets-refactor-design.md §3.2.
          window.S.plans[p.tipo]._createdAt = p.created_at;
        }
        seen.add(p.tipo);
      }
    });
  }
  updateDL();

  // Carica ultima misurazione
  await loadMeasurements();
  renderHomeDayCard();
  renderHomeMiniChart();

  // Messaggio di benvenuto / hero empty state.
  // Utente SENZA profilo (onboarding pending): inseriamo msg di tutorial AI
  // che guida verso la compilazione scheda. L'hero NON ha senso qui (lo
  // utente deve fare onboarding, non "modificare un piano").
  // Utente CON profilo: msgs resta vuoto → si mostra .chat-hero (logo +
  // saluto "Ciao Gabriele 👋" + starter cards). Sostituisce il vecchio
  // msg "Bentornato" come landing della chat (fix 2026-05-15).
  if (!window.S.profile) {
    // Se c'è un onboarding in corso salvato in localStorage (refresh/crash tab),
    // riprendi la conversazione senza ricominciare. Il save DB definitivo
    // partirà comunque al [PROFILO] finale (vedi send()).
    const savedConv = hydrateOnboardingConv();
    if (savedConv) {
      window.S.conv = savedConv;
      savedConv.forEach(m => addMsg(m.role === 'assistant' ? 'ai' : 'user', m.content));
    } else {
      addMsg('ai', 'Ciao! Sono il tuo **Coach AI** 💪\n\nGenero tre piani personalizzati — allenamento, alimentazione e stretching.\n\n**Veloce:** clicca 📋 per compilare la scheda e generare tutto senza domande.\n\nOppure dimmi come ti chiami e partiamo.');
    }
  }
  // else: utente con profilo → hero empty state visibile fino al primo msg.
}
